import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Icon } from '@/deploy/components/ui/Icon';
import { Badge } from '@/deploy/components/ui/Badge';
import { Button } from '@/deploy/components/ui/Button';
import { EnvBadge } from '@/deploy/components/ui/EnvBadge';
import { PageHeader } from '@/deploy/components/layout/PageHeader';
import { ConsoleModal } from '@/deploy/components/ui/ConsoleModal';
import { useSessionStore } from '@/deploy/stores/session.store';
import { environmentsService, type EnvironmentInstance } from '@/deploy/services/environments.service';
import { serverControlService, type ServerScope } from '@/deploy/services/server-control.service';
import type { EnvironmentId } from '@/deploy/types';
import { fmtUptime } from './DevEnvDetail';
import styles from './DevEnvDetail.module.scss';

const ENV_LABELS: Record<string, string> = { dev: 'Dev', beta: 'Bêta', preprod: 'Pré-prod', prod: 'Prod' };

export function InstanceDetail() {
  const { envId, identifier } = useParams<{ envId: string; identifier: string }>();
  const navigate = useNavigate();
  const project = useSessionStore((s) => s.currentProject());

  const [instance, setInstance] = useState<EnvironmentInstance | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<{ ramMb: number; cpuPct: number; uptimeSec: number } | null>(null);

  const scope = `mc-${envId}` as ServerScope;
  const backTo = `/deploiement/environments/${envId}`;
  // Seuls preprod/prod ont des instances "stables" → scope Ptero valide (mc-preprod/mc-prod).
  // dev/beta ont leurs propres pages de détail ; une URL forgée vers dev/beta tombe sur "introuvable".
  const isStableEnv = envId === 'preprod' || envId === 'prod';
  // Onglet Profileur : l'instance telle que le profileur la retrouve (identifiant Pterodactyl, nom, groupe).
  const profilerTarget = useMemo(
    () => (instance ? { identifier: instance.identifier, name: instance.name, group: instance.group ?? null } : undefined),
    [instance],
  );

  useEffect(() => {
    if (!isStableEnv) { setLoading(false); setInstance(null); setError('Environnement sans instance de ce type.'); return; }
    if (!project.id || !envId || !identifier) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    environmentsService.listInstances(project.id, envId as EnvironmentId)
      .then((r) => { if (!cancelled) setInstance(r.instances.find((i) => i.identifier === identifier) ?? null); })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : 'Erreur'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [project.id, envId, identifier, isStableEnv]);

  // Stats ressources (RAM / CPU / uptime) toutes les 3s.
  useEffect(() => {
    if (!instance || !project.id || !identifier) return;
    let alive = true;
    let t: ReturnType<typeof setTimeout> | null = null;
    const tick = async () => {
      try {
        const s = await serverControlService.state(project.id, scope, identifier);
        if (!alive) return;
        setStats({
          ramMb: Math.round((s.memoryBytes ?? 0) / (1024 * 1024)),
          cpuPct: Math.round((s.cpuAbsolute ?? 0) * 10) / 10,
          uptimeSec: Math.round((s.uptimeMs ?? 0) / 1000),
        });
      } catch { /* garde l'ancienne valeur */ }
      finally { if (alive) t = setTimeout(tick, 3000); }
    };
    void tick();
    return () => { alive = false; if (t) clearTimeout(t); };
  }, [instance, project.id, identifier, scope]);

  if (loading) {
    return <div className={styles.page}><div className={styles.empty}>Chargement de l'instance…</div></div>;
  }
  if (error || !instance) {
    return (
      <div className={styles.notFound}>
        <h2>Instance introuvable</h2>
        {error && <p style={{ color: 'var(--color-text-muted)' }}>{error}</p>}
        <Button variant="primary" onClick={() => navigate(backTo)}>Retour à l'environnement</Button>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <button className={styles.back} onClick={() => navigate(backTo)}>
        <Icon name="chevron-right" size={14} style={{ transform: 'rotate(180deg)' }} /> {ENV_LABELS[envId ?? ''] ?? 'Environnement'}
      </button>

      <PageHeader
        eyebrow={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <EnvBadge env={envId as EnvironmentId} size="sm" /> Instance Pterodactyl
          </span>
        }
        title={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <span className={styles.chip} style={{ background: '#F59E0B' }}><Icon name="server" size={20} /></span>
            {instance.name}
            {instance.containerCode && <Badge tone="info"><Icon name="package" size={11} /> {instance.containerCode}</Badge>}
          </span>
        }
        subtitle={`Ptero ${instance.identifier}`}
        // Image et ressources sur la ligne du nom de l'instance (à droite), plus de raccourcis /worlds et /mods.
        actions={
          <div className={styles.metaRow}>
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Image Docker</span>
              <code className={styles.metaValue} title={instance.image}>{instance.image}</code>
            </div>
            {stats && (
              <div className={styles.metaItem}>
                <span className={styles.metaLabel}>Ressources</span>
                <div className={styles.resourceRow}>
                  <div className={styles.resourceMetric}>
                    <span className={styles.resourceLabel}>Uptime</span>
                    <span className={styles.resourceValue}>{stats.uptimeSec > 0 ? fmtUptime(stats.uptimeSec) : '—'}</span>
                  </div>
                  <div className={styles.resourceMetric}>
                    <span className={styles.resourceLabel}>RAM</span>
                    <span className={styles.resourceValue}>{stats.ramMb} MB</span>
                  </div>
                  <div className={styles.resourceMetric}>
                    <span className={styles.resourceLabel}>CPU</span>
                    <span className={styles.resourceValue}>{stats.cpuPct}%</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        }
      />

      <ConsoleModal
        embedded
        open
        onClose={() => { /* embedded */ }}
        projectId={project.id}
        scope={scope}
        targetKey={instance.identifier}
        sourceName={instance.name}
        sourceSubtitle={`${ENV_LABELS[envId ?? ''] ?? envId} · ${instance.containerCode ?? ''}`}
        icon="server"
        crashTarget={{ env: envId as 'preprod' | 'prod', identifier: instance.identifier }}
        profilerTarget={profilerTarget}
      />
    </div>
  );
}
